import { AccountServiceProxy, IsTenantAvailableInput, IsTenantAvailableOutput } from '@shared/service-proxies/service-proxies';
import { map, Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { AbpSessionService } from 'abp-ng2-module';
import { AppTenantAvailabilityState } from '@shared/AppEnums';
import { AppConsts } from '@shared/AppConsts';

@Injectable({
    providedIn: 'root',
})
export class MultiTenancyService {
    private _accountServiceProxy = inject(AccountServiceProxy);
    private _sessionService = inject(AbpSessionService);

    checkTenantValidity(): Observable<boolean> {
        // an authenticated session takes its tenant from the token, never from the hostname
        if (this._sessionService.userId) {
            return of(true);
        }
        if (this.isHostHostname()) {
            abp.multiTenancy.setTenantIdCookie(null);
            return of(true);
        }
        return this.getTenantId().pipe(
            map((res) => {
                // deliberately no NotFound->host fallback: host lives only on hostHostnames, so an
                // unknown hostname is a bad tenant and must fail loudly instead of reaching host login
                // eslint-disable-next-line @typescript-eslint/no-unsafe-enums-comparison
                const tenantAvailable = !!res.tenantId && res.state === AppTenantAvailabilityState.Available;
                if (tenantAvailable) {
                    abp.multiTenancy.setTenantIdCookie(res.tenantId);
                }
                return tenantAvailable;
            }),
        );
    }

    /** Host administration is reachable only on its own hostname — there is no in-app switch into it. */
    protected isHostHostname(): boolean {
        return AppConsts.hostHostnames.includes(window.location.hostname);
    }

    protected getTenantId(): Observable<IsTenantAvailableOutput> {
        // same "first DNS label" rule as TenantManager.ReservedTenancyName() — keep the two in step
        const tenancyName = window.location.hostname.split('.')[0];
        return this._accountServiceProxy.isTenantAvailable(
            new IsTenantAvailableInput({
                tenancyName,
            }),
        );
    }
}
